import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  styled,
  TextField,
} from '@mui/material';
import PageHeader from '../../controls/PageHeader';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useRef, useState } from 'react';
import { CommonEntityState } from '../../../data/models/entity-state';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import {
  TransportationAttributeMenuItem,
  TransportationAttributeRowItem,
  TransportationAttributesGridContainer,
} from './TransportationAttributesGridContainer';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import { uniq } from 'lodash';
import { TransportationAttribute } from '../../../data/models/transportation-attribute.model';
import { hasWordStartingWith } from '../../../utils/string.utils';
import TransportationAttributeDialog, {
  TransportationAttributeDialogAction,
} from './TransportationAttributeDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import ActivateDialog from '../ActivateDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'TransportationAttributes';

const classes = {
  container: `${PREFIX}-container`,
  subHeader: `${PREFIX}-header`,
  contentContainer: `${PREFIX}-contentContainer`,
  search: `${PREFIX}-search`,
  statusSelect: `${PREFIX}-statusSelect`,
  addButton: `${PREFIX}-addButton`,
  moreButtonContainer: `${PREFIX}-moreButtonContainer`,
  moreButton: `${PREFIX}-moreButton`,
  clickableRow: `${PREFIX}-clickableRow`,
  nonClickableRow: `${PREFIX}-nonClickableRow`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(1),
  },
  [`& .${classes.contentContainer}`]: {
    padding: theme.spacing(2),
  },
  [`& .${classes.subHeader}`]: {
    justifyContent: 'space-between',
  },
  [`& .${classes.search}`]: {
    width: 320,
    marginRight: theme.spacing(3),
  },
  [`& .${classes.statusSelect}`]: {
    width: 200,
  },
  [`& .${classes.addButton}`]: {
    width: 150,
  },
  [`& .${classes.moreButtonContainer}`]: {
    display: 'flex',
    justifyContent: 'end',
    width: '100%',
  },
  [`& .${classes.moreButton}`]: {
    marginRight: theme.spacing(1),
  },
}));

export default function TransportationAttributes() {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();
  const allStatuses = t('transportationAttributes.all');
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(allStatuses);
  const [transAttrbDialogOpen, setTransAttrbDialogOpen] = useState(false);
  const [transAttrbDialogAction, setTransAttrbAction] = useState<
    TransportationAttributeDialogAction | undefined
  >(undefined);
  const [selectedTransAttrb, setSelectedTransAttrb] = useState<
    TransportationAttribute | undefined
  >(undefined);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [deleteAttributeUUID, setDeleteAttributeUUID] = useState('');
  const [isDeactivate, setIsDeactivate] = useState(false);
  const [activateDialogTitle, setActivateDialogTitle] = useState('');
  const [activateDialogMessage, setActivateDialogMessage] = useState('');
  const [activateDialogPostiveFn, setActivateDialogPostiveFn] = useState<
    () => void
  >(() => null);
  const [activateDialogNegativeFn, setActivateDialogNegativeFn] = useState<
    () => void
  >(() => null);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const transportationAttributesRef = useRef(transportationAttributes);
  // Save current value so that the handleMenuItemClick callback
  // can use the most recent value
  transportationAttributesRef.current = transportationAttributes;
  const loadingTransportationAttributes = useAppSelector(
    accountingSlice.selectLoadingTransportationAttributes,
  );

  const transportationAttributeCategories = useAppSelector(
    lookupSlice.selectTransportationAttributeCategories,
  );
  const selectableStatuses = [
    CommonEntityState.Active,
    CommonEntityState.Inactive,
    CommonEntityState.Draft,
  ];

  const statuses = selectableStatuses.map((status) => ({
    key: status as string,
    value: t(`common.${status}`),
  }));
  statuses.unshift({ key: allStatuses, value: allStatuses });

  const getCategoryName = (categoryBC: string) => {
    return (
      transportationAttributeCategories.find(
        (category) => category.transportationAttributeCategoryBC === categoryBC,
      )?.transportationAttributeCategoryName ?? ''
    );
  };

  const childCategories = uniq(
    transportationAttributes.map(
      (ta) => ta.childTransportationAttributeCategoryBC,
    ),
  );

  const rootTransAttrbs = transportationAttributes.filter(
    (ta) => !childCategories.includes(ta.transportationAttributeCategoryBC),
  );

  const isRootTransAttrb = (transAttrbUUID: string) =>
    rootTransAttrbs.find(
      (attrb) => attrb.transportationAttributeUUID === transAttrbUUID,
    );

  const getRowItem: (
    attrb: TransportationAttribute,
  ) => TransportationAttributeRowItem = (attrb: TransportationAttribute) => {
    return {
      transportationAttributeUUID: attrb.transportationAttributeUUID!,
      name: attrb.name,
      category: getCategoryName(attrb.transportationAttributeCategoryBC),
      status: attrb.state! as CommonEntityState,
      children: getRowChildren(attrb),
    };
  };

  const sortAttrbsByName = (rowItems: TransportationAttributeRowItem[]) =>
    rowItems.sort(
      (a: TransportationAttributeRowItem, b: TransportationAttributeRowItem) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );

  const getRowChildren: (
    attrb: TransportationAttribute,
  ) => TransportationAttributeRowItem[] = (attrb: TransportationAttribute) => {
    const children = transportationAttributes.filter(
      (ta) =>
        ta.transportationAttributeCategoryBC ===
        attrb.childTransportationAttributeCategoryBC,
    );
    const rowItems: TransportationAttributeRowItem[] = children.map((child) =>
      getRowItem(child),
    );
    return sortAttrbsByName(rowItems);
  };

  let rowItems = rootTransAttrbs
    .map((attrb) => {
      return getRowItem(attrb);
    })
    .filter(
      (rowItem) =>
        hasWordStartingWith(rowItem.name, searchText) ||
        hasWordStartingWith(rowItem.category, searchText),
    );
  rowItems = sortAttrbsByName(rowItems);

  useEffect(() => {
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
    dispatch(
      lookupSlice.fetchLookupItemsAsync('transportationAttributeCategory'),
    );
  }, []);

  useEffect(() => {
    const states =
      selectedStatus && selectedStatus !== allStatuses
        ? [selectedStatus]
        : undefined;
    dispatch(accountingSlice.fetchTransportationAttributesAsync(states));
  }, [selectedStatus]);

  const handleSearchTextChange = (val: string) => {
    setSearchText(val);
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleAddTransportationAttributeClick = () => {
    setTransAttrbAction(TransportationAttributeDialogAction.Add);
    setTransAttrbDialogOpen(true);
  };

  const handleTransAttrbDialogClose = () => {
    setSelectedTransAttrb(undefined);
    setTransAttrbDialogOpen(false);
  };

  const handleEditClick = (transportationAttributeUUID: string) => {
    const transAttrb = transportationAttributesRef.current.find(
      (attrb) =>
        attrb.transportationAttributeUUID === transportationAttributeUUID,
    );
    setSelectedTransAttrb(transAttrb);
    if (isSuperReaderUser) {
      setTransAttrbAction(TransportationAttributeDialogAction.View);
    } else {
      setTransAttrbAction(TransportationAttributeDialogAction.Edit);
    }
    setTransAttrbDialogOpen(true);
  };

  const handleActiveClick = (transportationAttributeUUID: string) => {
    activateTransportationAttribute(transportationAttributeUUID);
  };

  const handleInactiveClick = (transportationAttributeUUID: string) => {
    deactivateTransportationAttribute(transportationAttributeUUID);
  };

  const handleDeleteClick = (transportationAttributeUUID: string) => {
    setDeleteAttributeUUID(transportationAttributeUUID);
    setConfirmDeleteDialogOpen(true);
  };

  const handleMenuItemClick = (
    menuItem: TransportationAttributeMenuItem,
    transportationAttributeUUID: string,
  ) => {
    switch (menuItem) {
      case TransportationAttributeMenuItem.Edit:
        handleEditClick(transportationAttributeUUID);
        break;
      case TransportationAttributeMenuItem.Active:
        handleActiveClick(transportationAttributeUUID);
        break;
      case TransportationAttributeMenuItem.Inactive:
        handleInactiveClick(transportationAttributeUUID);
        break;
      case TransportationAttributeMenuItem.Delete:
        handleDeleteClick(transportationAttributeUUID);
        break;
    }
  };

  const confirmDelete = () => {
    dispatch(
      accountingSlice.deleteTransportationAttributeAsync(deleteAttributeUUID),
    );
    setConfirmDeleteDialogOpen(false);
  };

  const activateTransportationAttribute = (
    transportationAttributeUUID: string,
  ) => {
    const confirm = (start?: string, end?: string) => {
      updateTransportationAttributeEffectiveDates(
        transportationAttributeUUID,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(
      t('transportationAttributes.confirmActivateTransportationAttributeTitle'),
    );
    setActivateDialogMessage(
      t(
        'transportationAttributes.confirmActivateTransportationAttributeMessage',
      ),
    );
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivateTransportationAttribute = (
    transportationAttributeUUID: string,
  ) => {
    const confirm = (start?: string, end?: string) => {
      updateTransportationAttributeEffectiveDates(
        transportationAttributeUUID,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(
      t(
        'transportationAttributes.confirmDeactivateTransportationAttributeTitle',
      ),
    );
    setActivateDialogMessage(
      t(
        'transportationAttributes.confirmDeactivateTransportationAttributeMessage',
      ),
    );
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const updateTransportationAttributeEffectiveDates = (
    transportationAttributeUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      accountingSlice.updateTransportationAttributeEffectiveDatesAsync(
        transportationAttributeUUID,
        start,
        end,
      ),
    );
  };

  return (
    <StyledPaper>
      <Box className={classes.container}>
        <PageHeader
          title={t('transportationAttributes.title')}
          returnLink="/settings"
          returnLinkText={t('settings.title')}
        />
        <Stack direction="column" className={classes.contentContainer}>
          <Stack direction="row" className={classes.subHeader}>
            <Stack direction="row">
              <TextSearch
                id="text-search"
                className={classes.search}
                icon={<SearchIcon />}
                label={t('transportationAttributes.search')}
                currentSearchFilter={searchText}
                onSearchChange={handleSearchTextChange}
              />
              <TextField
                select
                className={classes.statusSelect}
                label={t('transportationAttributes.status')}
                variant="outlined"
                value={selectedStatus}
                onChange={(evt) => handleStatusChange(evt.target.value)}>
                {statuses?.map((status) => {
                  return (
                    <MenuItem value={status.key} key={status.key}>
                      {status.value}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Stack>
            {!isSuperReaderUser && (
              <Button
                className={classes.addButton}
                color="primary"
                variant="contained"
                size="large"
                onClick={handleAddTransportationAttributeClick}>
                {t('common.add')}
              </Button>
            )}
          </Stack>
          <TransportationAttributesGridContainer
            rowItems={rowItems}
            handleMenuItemClick={handleMenuItemClick}
            isLoading={loadingTransportationAttributes}
          />
        </Stack>
      </Box>
      <TransportationAttributeDialog
        open={transAttrbDialogOpen}
        action={transAttrbDialogAction}
        categories={transportationAttributeCategories}
        transAttrb={selectedTransAttrb}
        close={handleTransAttrbDialogClose}
        topLevel={
          !selectedTransAttrb ||
          !!isRootTransAttrb(
            selectedTransAttrb.transportationAttributeUUID ?? '',
          )
        }
      />
      <ActivateDialog
        title={activateDialogTitle}
        message={activateDialogMessage}
        open={activateDialogOpen}
        forActivate={!isDeactivate}
        handleOk={activateDialogPostiveFn}
        handleCancel={activateDialogNegativeFn}
      />
      <ConfirmationDialog
        title={t('transportationAttributes.confirmDeleteTitle')}
        message={t('transportationAttributes.confirmDeleteMessage')}
        open={confirmDeleteDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={() => setConfirmDeleteDialogOpen(false)}
      />
    </StyledPaper>
  );
}
